import { useEffect, useLayoutEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';

import { restoreKeyOf, shouldResetScroll } from './scroll-policy';
import { positionOf, rememberPosition } from './scroll-positions';

// Scroll al navegar (G4, cierre F1, QA DOC-49). La ventana es el contenedor de scroll.
//  - Otra pantalla (cambia la ruta, PUSH o REPLACE): abre arriba.
//  - Misma pantalla (cambian filtros, búsqueda o página en la URL): no se mueve.
//  - Atrás o adelante (POP): vuelve a la posición que tenía esa entrada del historial.
//  - Botón "Volver" de una pantalla de detalle: navega hacia adelante, pero trae la
//    entrada del origen y recupera su posición (QA F1 complemento UX-03).
// No se usa ScrollRestoration a secas porque sube la página en cada cambio de la URL,
// también en los filtros. La posición se guarda por entrada del historial (location.key).

/** Cuadros que se insiste en restaurar mientras el contenido termina de cargar. */
const RESTORE_FRAMES = 20;

/** Restaurando: los 'scroll' que dispara el propio scrollTo (recortados por una página
 *  aún corta) no son del usuario y no pisan la posición guardada. */
let restoring = false;
/** Intento de restauración vigente: una navegación nueva corta el anterior. */
let attempt = 0;

function restore(y: number, id: number, frame = 0): void {
  if (id !== attempt) {
    return;
  }
  restoring = true;
  window.scrollTo(0, y);
  if (frame < RESTORE_FRAMES && Math.abs(window.scrollY - y) > 1) {
    window.requestAnimationFrame(() => restore(y, id, frame + 1));
    return;
  }
  // El 'scroll' de este último scrollTo llega en el cuadro siguiente: recién ahí se
  // vuelve a guardar (si no, una posición recortada pisaría la guardada al rendirse).
  window.requestAnimationFrame(() => {
    if (id === attempt) {
      restoring = false;
    }
  });
}

export function ScrollManager(): null {
  const location = useLocation();
  const navType = useNavigationType();
  const current = useRef(location);
  const previous = useRef<{ pathname: string } | null>(null);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    const save = (): void => {
      if (!restoring) {
        rememberPosition(current.current.key, window.scrollY);
      }
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, []);

  useLayoutEffect(() => {
    current.current = location;
    const before = previous.current;
    previous.current = { pathname: location.pathname };
    if (before === null) {
      return;
    }
    attempt += 1;
    restoring = false;
    const origin = restoreKeyOf(location.state);
    if (navType === NavigationType.Pop) {
      restore(positionOf(location.key), attempt);
    } else if (origin !== null) {
      // La entrada nueva hereda la posición del origen: los 'scroll' de la restauración no
      // se guardan y, sin esto, la siguiente vuelta a esta entrada abriría arriba.
      const y = positionOf(origin);
      rememberPosition(location.key, y);
      restore(y, attempt);
    } else if (shouldResetScroll(before.pathname, location.pathname, navType)) {
      window.scrollTo(0, 0);
    }
  }, [location, navType]);

  return null;
}
